import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/joy';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Button } from '@/ui/Button';
import { RoundBadge } from '@/components/RoundBadge';
import { scrollToTopSmoothly } from '@/utils/scrollToTop';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';

const SHOW_AFTER_PX = 200;
const ICON_SIZE = 17;
const BUTTON_SIZE = 40;

export function ScrollShortcuts({ onOpenFilters, activeFilterCount }) {
  const [visible, setVisible] = useState(false);
  const scroller = useRef(window);

  // The window scrolls on mobile but <main> scrolls on desktop. Scroll events
  // don't bubble, so listen in the capture phase to catch whichever one it is.
  useEffect(() => {
    const onScroll = (event) => {
      const target = event.target;
      const isDocument = target === document;
      const top = isDocument ? window.scrollY : target.scrollTop;
      scroller.current = isDocument ? window : target;
      setVisible(top > SHOW_AFTER_PX);
    };
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  const scrollToTop = () => scrollToTopSmoothly(scroller.current);

  return (
    <>
      <FloatingButton visible={visible} label="Open filters" onClick={onOpenFilters} badgeCount={activeFilterCount}
        sx={{ top: 16, right: 16, transform: visible ? 'scale(1)' : 'scale(0.5)' }}>
        <TuneRoundedIcon />
      </FloatingButton>
      <FloatingButton visible={visible} label="Back to top" onClick={scrollToTop}
        sx={{ bottom: 78, right: 16, transform: visible ? 'scale(1)' : 'scale(0.5)' }}>
        <ArrowUpwardRoundedIcon />
      </FloatingButton>
    </>
  );
}

function FloatingButton({ visible, label, onClick, badgeCount, sx, children }) {
  const button = (
    <Button
      color="black"
      type="icon"
      shape="pill"
      aria-label={label}
      onClick={onClick}
      sx={{ width: BUTTON_SIZE, height: BUTTON_SIZE, boxShadow: 'var(--joy-shadow-md)', '& .MuiSvgIcon-root': { fontSize: ICON_SIZE, color: '#fff' }, 
            boxShadow: '0 8px 18px rgba(0, 0, 0, 0.26)' }}
    >
      {children}
    </Button>
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 1100,
        opacity: visible ? 1 : 0,
        visibility: visible ? 'visible' : 'hidden',
        transition: 'opacity 200ms ease, transform 200ms ease, visibility 0s linear ' + (visible ? '0s' : '200ms'),
        ...sx,
      }}
    >
      {badgeCount === undefined ? button : <RoundBadge badgeContent={badgeCount} badgeInset="10%">{button}</RoundBadge>}
    </Box>
  );
}
